/** @file Local rotation that aims a bone from its joint at a world target under an effective parent. */
import { angle } from '@pose2d/math/angle';
import { trig } from '@pose2d/math/trig';
import type { Affine } from '@pose2d/math/types';

/**
 * Degrees: atan2 of inverse(E) x (target - joint) minus shearX. Only the linear part of `e` is read. A singular
 * `e` falls back to the world angle (as if E = I); a target on the joint gives 0 - shearX (callers skip that case).
 */
export function aimLocalRotation(e: Affine, jointX: number, jointY: number, targetX: number, targetY: number, shearX: number): number {
  const a = e[0] ?? 0;
  const b = e[1] ?? 0;
  const c = e[2] ?? 0;
  const d = e[3] ?? 0;
  const dx = targetX - jointX;
  const dy = targetY - jointY;
  const det = a * d - b * c;
  if (det === 0 || !Number.isFinite(det)) return angle.radToDeg(trig.atan2(dy, dx)) - shearX;
  // inverse(E)·(dx, dy) = (d·dx − b·dy, a·dy − c·dx) / det; only its direction matters, so scale by sign(det).
  const s = det > 0 ? 1 : -1;
  return angle.radToDeg(trig.atan2(s * (a * dy - c * dx), s * (d * dx - b * dy))) - shearX;
}
